import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface OrdemServico {
  id: string;
  account_id?: string;
  condominio_id: string;
  prestador_id?: string;
  infraestrutura_id?: string;
  titulo: string;
  descricao?: string;
  data_programada?: string;
  data_execucao?: string;
  custo: number;
  status: string;
  observacoes?: string;
  created_at: string;
  updated_at?: string;
}

interface CondominioOption {
  id: string;
  nome: string;
}

interface PrestadorOption {
  id: string;
  nome: string;
  tipo: string;
  servicos: string[];
  // A consulta seleciona `ativo`; registros antigos podem vir sem o campo.
  ativo?: boolean;
}

interface InfraOption {
  id: string;
  nome: string;
  categoria: string;
}

const statusOptions = [
  {
    value: 'pendente',
    label: 'Pendente',
    icon: 'ri-time-line',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    value: 'em_andamento',
    label: 'Em Andamento',
    icon: 'ri-loader-4-line',
    color: 'bg-accent-50 text-accent-700 border-accent-200',
  },
  {
    value: 'concluida',
    label: 'Concluída',
    icon: 'ri-check-double-line',
    color: 'bg-green-50 text-green-700 border-green-200',
  },
  {
    value: 'cancelada',
    label: 'Cancelada',
    icon: 'ri-close-circle-line',
    color: 'bg-red-50 text-red-700 border-red-200',
  },
];

function getStatusInfo(status: string) {
  return (
    statusOptions.find((s) => s.value === status) || {
      label: status,
      icon: 'ri-question-line',
      color: 'bg-background-100 text-foreground-700 border-background-300',
    }
  );
}

function formatCurrency(valor: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
}

function formatDate(iso?: string) {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/* ==================== MODAL DE FORMULÁRIO ==================== */
function OSFormModal({
  ordem,
  condominios,
  prestadores,
  onSave,
  onClose,
}: {
  ordem: OrdemServico | null;
  condominios: CondominioOption[];
  prestadores: PrestadorOption[];
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    condominio_id: ordem?.condominio_id || '',
    prestador_id: ordem?.prestador_id || '',
    infraestrutura_id: ordem?.infraestrutura_id || '',
    titulo: ordem?.titulo || '',
    descricao: ordem?.descricao || '',
    data_programada: ordem?.data_programada || '',
    data_execucao: ordem?.data_execucao || '',
    custo: ordem?.custo?.toString() || '',
    status: ordem?.status || 'pendente',
    observacoes: ordem?.observacoes || '',
  });

  const [infraItems, setInfraItems] = useState<InfraOption[]>([]);
  const [loadingInfra, setLoadingInfra] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!form.condominio_id) {
      setInfraItems([]);
      return;
    }
    setLoadingInfra(true);
    // O builder do supabase-js e apenas "thenable": Promise.resolve da acesso a .finally.
    Promise.resolve(
      supabase
        .from('infraestrutura')
        .select('id, nome, categoria')
        .eq('condominio_id', form.condominio_id)
        .order('nome', { ascending: true }),
    )
      .then(({ data, error }) => {
        if (!error && data) setInfraItems(data as InfraOption[]);
      })
      .finally(() => setLoadingInfra(false));
  }, [form.condominio_id]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.condominio_id.trim()) errs.condominio_id = 'Selecione um condomínio';
    if (!form.titulo.trim()) errs.titulo = 'Título é obrigatório';
    if (!form.data_programada.trim()) errs.data_programada = 'Data programada é obrigatória';
    if (!form.custo.trim() || isNaN(parseFloat(form.custo))) errs.custo = 'Informe um custo válido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      condominio_id: form.condominio_id,
      prestador_id: form.prestador_id || null,
      infraestrutura_id: form.infraestrutura_id || null,
      titulo: form.titulo.trim(),
      descricao: form.descricao.trim() || null,
      data_programada: form.data_programada,
      data_execucao: form.data_execucao || null,
      custo: parseFloat(form.custo),
      status: form.status,
      observacoes: form.observacoes.trim() || null,
    });
  };

  const prestadoresAtivos = prestadores.filter((p) => p.ativo !== false);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[4vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-2xl shadow-lg max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between sticky top-0 bg-background-50 rounded-t-lg z-10">
          <h3 className="text-sm font-semibold text-foreground-900">
            {ordem ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-5">
          {/* Condomínio */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Condomínio *
            </label>
            <select
              value={form.condominio_id}
              onChange={(e) =>
                setForm({ ...form, condominio_id: e.target.value, infraestrutura_id: '' })
              }
              className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.condominio_id ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
            >
              <option value="">Selecionar condomínio...</option>
              {condominios.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            {errors.condominio_id && (
              <p className="text-xs text-red-600 mt-1">{errors.condominio_id}</p>
            )}
          </div>

          {/* Título + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Título *
              </label>
              <input
                type="text"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Manutenção preventiva de elevadores"
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.titulo ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.titulo && <p className="text-xs text-red-600 mt-1">{errors.titulo}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Prestador + Item Infra */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-team-line text-xs"></i>
                </div>
                Prestador
              </label>
              <select
                value={form.prestador_id}
                onChange={(e) => setForm({ ...form, prestador_id: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="">Sem prestador vinculado</option>
                {prestadoresAtivos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome} ({p.tipo === 'pj' ? 'PJ' : 'PF'})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-tools-line text-xs"></i>
                </div>
                Item de Infraestrutura
              </label>
              <select
                value={form.infraestrutura_id}
                onChange={(e) => setForm({ ...form, infraestrutura_id: e.target.value })}
                disabled={!form.condominio_id || loadingInfra}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm disabled:opacity-50"
              >
                <option value="">
                  {!form.condominio_id
                    ? 'Selecione um condomínio primeiro'
                    : loadingInfra
                      ? 'Carregando...'
                      : infraItems.length === 0
                        ? 'Nenhum item cadastrado'
                        : 'Sem item vinculado'}
                </option>
                {infraItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nome} — {item.categoria}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Datas + Custo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Data Programada *
              </label>
              <input
                type="date"
                value={form.data_programada}
                onChange={(e) => setForm({ ...form, data_programada: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.data_programada ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.data_programada && (
                <p className="text-xs text-red-600 mt-1">{errors.data_programada}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Data Execução
              </label>
              <input
                type="date"
                value={form.data_execucao}
                onChange={(e) => setForm({ ...form, data_execucao: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Custo (R$) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.custo}
                onChange={(e) => setForm({ ...form, custo: e.target.value })}
                placeholder="0,00"
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.custo ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.custo && <p className="text-xs text-red-600 mt-1">{errors.custo}</p>}
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Descrição
            </label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-y"
              placeholder="Detalhes do serviço a ser executado..."
            />
            <p className="text-xs text-foreground-400 mt-1">
              {form.descricao.length}/500 caracteres
            </p>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Observações
            </label>
            <textarea
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              maxLength={500}
              rows={2}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-y"
              placeholder="Informações adicionais..."
            />
            <p className="text-xs text-foreground-400 mt-1">
              {form.observacoes.length}/500 caracteres
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {ordem ? 'Salvar Alterações' : 'Criar Ordem de Serviço'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ==================== MODAL DE DETALHE ==================== */
function OSDetailModal({
  ordem,
  condominios,
  prestadores,
  infraItems,
  onEdit,
  onClose,
  onDelete,
}: {
  ordem: OrdemServico;
  condominios: CondominioOption[];
  prestadores: PrestadorOption[];
  infraItems: InfraOption[];
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const condoNome = condominios.find((c) => c.id === ordem.condominio_id)?.nome || '—';
  const prestadorNome = ordem.prestador_id
    ? prestadores.find((p) => p.id === ordem.prestador_id)?.nome || '—'
    : 'Não vinculado';
  const prestadorTipo = ordem.prestador_id
    ? prestadores.find((p) => p.id === ordem.prestador_id)?.tipo || ''
    : '';
  const infraNome = ordem.infraestrutura_id
    ? infraItems.find((item) => item.id === ordem.infraestrutura_id)?.nome || '—'
    : 'Não vinculado';
  const statusInfo = getStatusInfo(ordem.status);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[6vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-xl shadow-lg max-h-[88vh] overflow-y-auto">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between sticky top-0 bg-background-50 rounded-t-lg z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${statusInfo.color.split(' ')[0]}`}
            >
              <i className={`${statusInfo.icon} ${statusInfo.color.split(' ')[1]}`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground-900 truncate">{ordem.titulo}</h3>
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <span>{condoNome}</span>
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color}`}
                >
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-5">
          {/* Datas e custo */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-background-100/50 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-foreground-500 mb-1">Programada</p>
              <p className="text-sm font-semibold text-foreground-800">
                {formatDate(ordem.data_programada)}
              </p>
            </div>
            <div className="bg-background-100/50 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-foreground-500 mb-1">Execução</p>
              <p className="text-sm font-semibold text-foreground-800">
                {formatDate(ordem.data_execucao)}
              </p>
            </div>
            <div className="bg-background-100/50 rounded-lg p-3 text-center">
              <p className="text-xs font-medium text-foreground-500 mb-1">Custo</p>
              <p className="text-sm font-semibold text-foreground-800">
                {formatCurrency(ordem.custo)}
              </p>
            </div>
          </div>

          {/* Vínculos */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-background-100/50 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-building-line text-xs"></i>
                </div>
                Condomínio
              </p>
              <p className="text-sm text-foreground-800">{condoNome}</p>
            </div>
            <div className="bg-background-100/50 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-team-line text-xs"></i>
                </div>
                Prestador
              </p>
              <p className="text-sm text-foreground-800">{prestadorNome}</p>
              {prestadorTipo && (
                <p className="text-xs text-foreground-500 mt-0.5">
                  {prestadorTipo === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                </p>
              )}
            </div>
            <div className="bg-background-100/50 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-tools-line text-xs"></i>
                </div>
                Infraestrutura
              </p>
              <p className="text-sm text-foreground-800">{infraNome}</p>
            </div>
          </div>

          {/* Descrição */}
          {ordem.descricao && (
            <div>
              <p className="text-xs font-semibold text-foreground-700 mb-1.5 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-file-text-line text-xs"></i>
                </div>
                Descrição
              </p>
              <div className="bg-background-100/50 rounded-lg p-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{ordem.descricao}</p>
              </div>
            </div>
          )}

          {/* Observações */}
          {ordem.observacoes && (
            <div>
              <p className="text-xs font-semibold text-foreground-700 mb-1.5 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-sticky-note-line text-xs"></i>
                </div>
                Observações
              </p>
              <div className="bg-background-100/50 rounded-lg p-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{ordem.observacoes}</p>
              </div>
            </div>
          )}

          {/* Datas de registro */}
          <div className="text-xs text-foreground-400 flex items-center gap-4">
            <span>Criado em: {formatDate(ordem.created_at?.slice(0, 10))}</span>
            {ordem.updated_at && (
              <span>Atualizado em: {formatDate(ordem.updated_at?.slice(0, 10))}</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-background-200/70 flex justify-between sticky bottom-0 bg-background-50 rounded-b-lg">
          <button
            onClick={onDelete}
            className="px-3 py-2 rounded-md border border-red-200 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line mr-1.5"></i>Excluir
          </button>
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line mr-1.5"></i>Editar
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== PÁGINA PRINCIPAL ==================== */
export default function OrdensServicoPage() {
  const { isLoggedIn, effectiveAccountId, isSuperuser } = useAuth();
  const [ordens, setOrdens] = useState<OrdemServico[]>([]);
  const [condominios, setCondominios] = useState<CondominioOption[]>([]);
  const [prestadores, setPrestadores] = useState<PrestadorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCondominio, setFiltroCondominio] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [infraItems, setInfraItems] = useState<InfraOption[]>([]);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [detailTarget, setDetailTarget] = useState<OrdemServico | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<OrdemServico | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrdemServico | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordensRes, condosRes, prestRes, infraRes] = await Promise.all([
        supabase.from('ordens_servico').select('*').order('data_programada', { ascending: false }),
        supabase.from('condominios').select('id, nome').order('nome', { ascending: true }),
        supabase
          .from('prestadores')
          .select('id, nome, tipo, servicos, ativo')
          .order('nome', { ascending: true }),
        supabase
          .from('infraestrutura')
          .select('id, nome, categoria')
          .order('nome', { ascending: true }),
      ]);

      if (ordensRes.error) {
        setError(ordensRes.error.message);
      } else {
        setOrdens((ordensRes.data || []) as OrdemServico[]);
      }
      if (!condosRes.error) setCondominios((condosRes.data || []) as CondominioOption[]);
      if (!prestRes.error) setPrestadores((prestRes.data || []) as PrestadorOption[]);
      if (!infraRes.error) setInfraItems((infraRes.data || []) as InfraOption[]);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (formData: any) => {
    if (editTarget) {
      const { error: uErr } = await supabase
        .from('ordens_servico')
        .update(formData)
        .eq('id', editTarget.id);
      if (uErr) {
        showToast(`Erro ao atualizar: ${uErr.message}`, 'error');
        return;
      }
      showToast(`"${formData.titulo}" atualizada com sucesso.`, 'success');
    } else {
      // `account_id` não vai no payload: o gatilho `ordens_servico_conta`
      // (migration 0033) o deriva do condomínio. Mandá-lo daqui seria deixar o
      // formulário escolher o Tenant — e foi a ausência dessa regra que deixou
      // as 26 ordens do seed sem conta, invisíveis para a RLS.
      const { error: iErr } = await supabase.from('ordens_servico').insert([formData]);
      if (iErr) {
        showToast(`Erro ao criar: ${iErr.message}`, 'error');
        return;
      }
      showToast(`"${formData.titulo}" criada com sucesso.`, 'success');
    }
    setShowForm(false);
    setEditTarget(null);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase
      .from('ordens_servico')
      .delete()
      .eq('id', deleteTarget.id);
    if (dErr) {
      showToast(`Erro ao excluir: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }
    showToast(`"${deleteTarget.titulo}" excluída.`, 'success');
    setDeleteTarget(null);
    fetchData();
  };

  if (!isLoggedIn) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Faça login para acessar as ordens de serviço.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-500">Carregando ordens de serviço...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4 text-center max-w-md">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900">Erro ao carregar</h2>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={fetchData}
              className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const filtrados = ordens.filter((os) => {
    const matchBusca =
      os.titulo.toLowerCase().includes(busca.toLowerCase()) ||
      (os.descricao || '').toLowerCase().includes(busca.toLowerCase()) ||
      (getCondominioNome(os.condominio_id) || '').toLowerCase().includes(busca.toLowerCase()) ||
      (getPrestadorNome(os.prestador_id) || '').toLowerCase().includes(busca.toLowerCase());
    const matchStatus = filtroStatus ? os.status === filtroStatus : true;
    const matchCondominio = filtroCondominio ? os.condominio_id === filtroCondominio : true;
    const matchDataInicio = dataInicio ? (os.data_programada || '') >= dataInicio : true;
    const matchDataFim = dataFim ? (os.data_programada || '') <= dataFim : true;
    return matchBusca && matchStatus && matchCondominio && matchDataInicio && matchDataFim;
  });

  function getCondominioNome(id?: string) {
    if (!id) return null;
    return condominios.find((c) => c.id === id)?.nome || null;
  }

  function getPrestadorNome(id?: string) {
    if (!id) return null;
    return prestadores.find((p) => p.id === id)?.nome || null;
  }

  function getInfraNome(id?: string) {
    if (!id) return null;
    return infraItems.find((item) => item.id === id)?.nome || null;
  }

  const pendentes = ordens.filter((o) => o.status === 'pendente').length;
  const emAndamento = ordens.filter((o) => o.status === 'em_andamento').length;
  const concluidas = ordens.filter((o) => o.status === 'concluida').length;
  const canceladas = ordens.filter((o) => o.status === 'cancelada').length;

  const custoTotal = ordens.reduce((sum, o) => sum + (o.custo || 0), 0);
  const custoConcluidas = ordens
    .filter((o) => o.status === 'concluida')
    .reduce((sum, o) => sum + (o.custo || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Ordens de Serviço</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Gerencie as execuções de manutenção vinculando prestadores e itens de infraestrutura.
            </p>
          </div>
          <button
            onClick={() => {
              setEditTarget(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>Nova Ordem de Serviço
          </button>
        </div>

        <div className="flex flex-col gap-6">
          {/* Cards resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              {
                label: 'Total',
                valor: ordens.length,
                icon: 'ri-file-list-3-line',
                bg: 'bg-primary-50',
                iconColor: 'text-primary-600',
              },
              {
                label: 'Pendentes',
                valor: pendentes,
                icon: 'ri-time-line',
                bg: 'bg-amber-50',
                iconColor: 'text-amber-600',
              },
              {
                label: 'Em Andamento',
                valor: emAndamento,
                icon: 'ri-loader-4-line',
                bg: 'bg-accent-50',
                iconColor: 'text-accent-600',
              },
              {
                label: 'Concluídas',
                valor: concluidas,
                icon: 'ri-check-double-line',
                bg: 'bg-green-50',
                iconColor: 'text-green-600',
              },
              {
                label: 'Canceladas',
                valor: canceladas,
                icon: 'ri-close-circle-line',
                bg: 'bg-red-50',
                iconColor: 'text-red-600',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                    <i className={`${card.icon} ${card.iconColor} text-sm`}></i>
                  </div>
                  <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                </div>
                <p className="text-2xl font-bold text-foreground-900">{card.valor}</p>
              </div>
            ))}
          </div>

          {/* Custo resumo */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-green-600"></i>
              </div>
              <div>
                <p className="text-xs font-medium text-foreground-500">Custo total em ordens</p>
                <p className="text-lg font-bold text-foreground-900">
                  {formatCurrency(custoTotal)}
                </p>
              </div>
            </div>
            <div className="flex-1 bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-double-line text-primary-600"></i>
              </div>
              <div>
                <p className="text-xs font-medium text-foreground-500">
                  Custo já executado (concluídas)
                </p>
                <p className="text-lg font-bold text-foreground-900">
                  {formatCurrency(custoConcluidas)}
                </p>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar por título, condomínio ou prestador..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center text-foreground-400 flex-shrink-0">
                <i className="ri-calendar-line text-xs"></i>
              </div>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm w-[150px]"
                title="Data início"
              />
              <span className="text-foreground-400 text-xs">até</span>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm w-[150px]"
                title="Data fim"
              />
              {(dataInicio || dataFim) && (
                <button
                  onClick={() => {
                    setDataInicio('');
                    setDataFim('');
                  }}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                  title="Limpar filtro de data"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-sm font-medium whitespace-nowrap ${
                  filtroCondominio
                    ? 'bg-primary-50 border-primary-300 text-primary-800'
                    : 'bg-background-50 border-background-300/60 text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i
                  className={`text-base ${filtroCondominio ? 'ri-building-2-fill' : 'ri-building-2-line'}`}
                ></i>
                <span>
                  {filtroCondominio
                    ? condominios.find((c) => c.id === filtroCondominio)?.nome || ''
                    : 'Todos os condomínios'}
                </span>
                <i
                  className={`${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs transition-transform`}
                ></i>
              </button>

              {filterOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-20 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto py-1">
                      <button
                        onClick={() => {
                          setFiltroCondominio('');
                          setFilterOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          !filtroCondominio
                            ? 'bg-primary-50 text-primary-800 font-medium'
                            : 'text-foreground-700 hover:bg-background-100'
                        }`}
                      >
                        <i className="ri-global-line text-base"></i>
                        <span>Todos os condomínios</span>
                        {!filtroCondominio && (
                          <i className="ri-check-line ml-auto text-primary-600"></i>
                        )}
                      </button>
                      {condominios.map((condo) => (
                        <button
                          key={condo.id}
                          onClick={() => {
                            setFiltroCondominio(condo.id);
                            setFilterOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                            filtroCondominio === condo.id
                              ? 'bg-primary-50 text-primary-800 font-medium'
                              : 'text-foreground-700 hover:bg-background-100'
                          }`}
                        >
                          <i className="ri-building-2-line text-base"></i>
                          <span>{condo.nome}</span>
                          {filtroCondominio === condo.id && (
                            <i className="ri-check-line ml-auto text-primary-600"></i>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {filtroCondominio && (
              <button
                onClick={() => setFiltroCondominio('')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                <span>Limpar filtro</span>
              </button>
            )}
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Todos os status</option>
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tabela */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Ordem de Serviço
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      Condomínio
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                      Prestador
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                      Infraestrutura
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Datas
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Custo
                    </th>
                    <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {filtrados.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                          <i className="ri-file-list-3-line text-foreground-400 text-xl"></i>
                        </div>
                        <p className="text-sm text-foreground-500">
                          {ordens.length === 0
                            ? 'Nenhuma ordem de serviço cadastrada ainda.'
                            : 'Nenhuma ordem de serviço encontrada com os filtros atuais.'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filtrados.map((os) => {
                      const statusInfo = getStatusInfo(os.status);
                      const condoNome = getCondominioNome(os.condominio_id);
                      const prestadorNome = getPrestadorNome(os.prestador_id);
                      return (
                        <tr key={os.id} className="hover:bg-background-100/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${statusInfo.color.split(' ')[0]}`}
                              >
                                <i
                                  className={`${statusInfo.icon} ${statusInfo.color.split(' ')[1]} text-sm`}
                                ></i>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground-800 truncate">
                                  {os.titulo}
                                </p>
                                {os.descricao && (
                                  <p className="text-xs text-foreground-500 truncate mt-0.5">
                                    {os.descricao}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <p className="text-sm text-foreground-700 whitespace-nowrap">
                              {condoNome || '—'}
                            </p>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <p className="text-sm text-foreground-700 whitespace-nowrap">
                              {prestadorNome || (
                                <span className="text-foreground-400 italic">Não vinculado</span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3 hidden xl:table-cell">
                            <p className="text-sm text-foreground-700 whitespace-nowrap">
                              {getInfraNome(os.infraestrutura_id) || (
                                <span className="text-foreground-400 italic">—</span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                                  <i className="ri-calendar-line text-xs"></i>
                                </div>
                                <span className="text-foreground-700 font-medium">
                                  {formatDate(os.data_programada)}
                                </span>
                              </div>
                              {os.data_execucao && (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <div className="w-3.5 h-3.5 flex items-center justify-center text-green-500">
                                    <i className="ri-check-line text-xs"></i>
                                  </div>
                                  <span className="text-foreground-500">
                                    {formatDate(os.data_execucao)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                              {formatCurrency(os.custo)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${statusInfo.color}`}
                            >
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setDetailTarget(os)}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                                title="Ver detalhes"
                              >
                                <i className="ri-eye-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => {
                                  setEditTarget(os);
                                  setShowForm(true);
                                }}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                                title="Editar"
                              >
                                <i className="ri-pencil-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => setDeleteTarget(os)}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors cursor-pointer"
                                title="Excluir"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de formulário */}
      {showForm && (
        <OSFormModal
          ordem={editTarget}
          condominios={condominios}
          prestadores={prestadores}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* Modal de detalhe */}
      {detailTarget && (
        <OSDetailModal
          ordem={detailTarget}
          condominios={condominios}
          prestadores={prestadores}
          infraItems={infraItems}
          onEdit={() => {
            setEditTarget(detailTarget);
            setDetailTarget(null);
            setShowForm(true);
          }}
          onClose={() => setDetailTarget(null)}
          onDelete={() => {
            setDeleteTarget(detailTarget);
            setDetailTarget(null);
          }}
        />
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir a ordem de serviço{' '}
              <strong>{deleteTarget.titulo}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-background-50 text-foreground-800 border border-background-200'
            }`}
          >
            <i
              className={`${
                toast.type === 'success'
                  ? 'ri-checkbox-circle-line text-green-600'
                  : toast.type === 'error'
                    ? 'ri-close-circle-line text-red-600'
                    : 'ri-information-line text-foreground-500'
              }`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
